import { styled } from "styled-components";
import { Button, Form, Input, Select } from "antd";
import { ReactNode } from "react";
import {
  MdAdd,
  MdBusinessCenter,
  MdClose,
  MdGroups,
  MdHandyman,
  MdMovieCreation,
  MdPersonOutline,
} from "react-icons/md";
import { ProfileAutocomplete } from "../../components/autocomplete.component";
import { SetState, StatePair } from "../../../../types/SetState";
import { v4 } from "uuid";
import { masterToOptions } from "../../../../utils/functions/misc/masterToOptions";
import { useAtomValue } from "jotai";
import { masterAtom } from "../../../../atoms/masters.atom";
import { Subject } from "rxjs";
import { colors } from "../../../../theme/colors";
import { Section, card } from "../../components/section-card.component";

export const StepFour = ({
  directors,
  setDirectors,
  producers,
  setProducers,
  cast,
  setCast,
  crew,
  setCrew,
  nameOptions,
  professionalProfileSubject$,
  setNameModalType,
  visible = true,
}: StepFourProps) => {
  const master = useAtomValue(masterAtom);

  return (
    <CastCrewWrapper style={{ display: visible ? "flex" : "none" }}>
      <CastCrewGrid>
        <Section
          icon={<MdMovieCreation />}
          title="Directors"
          subtitle="Everyone credited with directing this film."
        >
          <PeopleList
            count={directors.length}
            emptyTitle="No directors added yet"
            emptyHint="Add the people who directed this film."
            addLabel="Add director"
            onAdd={() =>
              setDirectors([
                ...directors,
                { name: "", biography: "", id: v4() },
              ])
            }
          >
            {directors.map((director, index) => (
              <PersonRow
                key={director.id ?? `director-${index}`}
                logo={director.logo}
                canRemove={directors.length > 1}
                onRemove={() => {
                  const next = [...directors];
                  next.splice(index, 1);
                  setDirectors(next);
                }}
              >
                <ProfileAutocomplete
                  targetNameModalType={0}
                  name={director.name}
                  index={index}
                  nameOptions={nameOptions}
                  setNameModalType={setNameModalType}
                  subject$={professionalProfileSubject$}
                  array={directors}
                  setArray={setDirectors}
                  dataProperty="biography"
                />
              </PersonRow>
            ))}
          </PeopleList>
        </Section>

        <Section
          icon={<MdBusinessCenter />}
          title="Producers"
          subtitle="Everyone credited with producing this film."
        >
          <PeopleList
            count={producers.length}
            emptyTitle="No producers added yet"
            emptyHint="Add the producers backing this film."
            addLabel="Add producer"
            onAdd={() =>
              setProducers([
                ...producers,
                { name: "", filmography: "", id: v4() },
              ])
            }
          >
            {producers.map((producer, index) => (
              <PersonRow
                key={producer.id ?? `producer-${index}`}
                logo={producer.logo}
                canRemove={producers.length > 1}
                onRemove={() => {
                  const next = [...producers];
                  next.splice(index, 1);
                  setProducers(next);
                }}
              >
                <ProfileAutocomplete
                  targetNameModalType={1}
                  name={producer.name}
                  index={index}
                  nameOptions={nameOptions}
                  setNameModalType={setNameModalType}
                  subject$={professionalProfileSubject$}
                  array={producers}
                  setArray={setProducers}
                  dataProperty="filmography"
                />
              </PersonRow>
            ))}
          </PeopleList>
        </Section>

        <Section
          icon={<MdGroups />}
          title="Cast"
          subtitle="Actors and the characters they play."
        >
          <PeopleList
            count={cast.length}
            emptyTitle="No cast added yet"
            emptyHint="Add the actors and their characters."
            addLabel="Add cast member"
            onAdd={() =>
              setCast([...cast, { name: "", character: "", id: v4() }])
            }
          >
            {cast.map((member, index) => (
              <PersonRow
                key={member.id ?? `cast-${index}`}
                logo={member.logo}
                onRemove={() => {
                  const next = [...cast];
                  next.splice(index, 1);
                  setCast(next);
                }}
              >
                <FieldStack>
                  <ProfileAutocomplete
                    targetNameModalType={2}
                    name={member.name}
                    index={index}
                    nameOptions={nameOptions}
                    setNameModalType={setNameModalType}
                    subject$={professionalProfileSubject$}
                    array={cast}
                    setArray={setCast}
                    dataProperty="character"
                  />
                  <Form.Item
                    name={`character.${member.id}`}
                    rules={[
                      {
                        required: true,
                        message: "Please enter a character name!",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      placeholder="Character name"
                      value={member.character}
                      onChange={(evt) => {
                        const next = [...cast];
                        next[index].character = evt.target.value;
                        setCast(next);
                      }}
                    />
                  </Form.Item>
                </FieldStack>
              </PersonRow>
            ))}
          </PeopleList>
        </Section>

        <Section
          icon={<MdHandyman />}
          title="Crew"
          subtitle="The people behind the scenes and their roles."
        >
          <PeopleList
            count={crew.length}
            emptyTitle="No crew added yet"
            emptyHint="Add the people behind the camera."
            addLabel="Add crew member"
            onAdd={() => setCrew([...crew, { name: "", role: "", id: v4() }])}
          >
            {crew.map((member, index) => (
              <PersonRow
                key={member.id ?? `crew-${index}`}
                logo={member.logo}
                onRemove={() => {
                  const next = [...crew];
                  next.splice(index, 1);
                  setCrew(next);
                }}
              >
                <FieldStack>
                  <ProfileAutocomplete
                    targetNameModalType={3}
                    name={member.name}
                    index={index}
                    nameOptions={nameOptions}
                    setNameModalType={setNameModalType}
                    subject$={professionalProfileSubject$}
                    array={crew}
                    setArray={setCrew}
                    dataProperty="role"
                  />
                  <Form.Item
                    name={`role.${index}`}
                    initialValue={member.role}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      options={masterToOptions("crew_role", master)}
                      placeholder="Select role"
                      onSelect={(role) => {
                        const next = [...crew];
                        next[index].role = role;
                        setCrew(next);
                      }}
                      value={member.role}
                      showSearch
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </FieldStack>
              </PersonRow>
            ))}
          </PeopleList>
        </Section>
      </CastCrewGrid>
    </CastCrewWrapper>
  );
};

const PeopleList = ({
  count,
  emptyTitle,
  emptyHint,
  addLabel,
  onAdd,
  children,
}: {
  count: number;
  emptyTitle: string;
  emptyHint: string;
  addLabel: string;
  onAdd: () => void;
  children: ReactNode;
}) => (
  <ListShell>
    {count === 0 ? (
      <EmptyState>
        <EmptyIcon>
          <MdPersonOutline />
        </EmptyIcon>
        <EmptyTitle>{emptyTitle}</EmptyTitle>
        <EmptyHint>{emptyHint}</EmptyHint>
      </EmptyState>
    ) : (
      <RowList>{children}</RowList>
    )}
    <AddButton
      type="dashed"
      block
      icon={<MdAdd />}
      onClick={onAdd}
    >
      {addLabel}
    </AddButton>
  </ListShell>
);

const PersonRow = ({
  logo,
  onRemove,
  canRemove = true,
  children,
}: {
  logo?: string;
  onRemove: () => void;
  canRemove?: boolean;
  children: ReactNode;
}) => (
  <RowShell>
    <Avatar src={logo || "/images/ui/avatar.png"} />
    <RowBody>{children}</RowBody>
    {canRemove ? (
      <RemoveBtn type="button" onClick={onRemove} aria-label="Remove">
        <MdClose />
      </RemoveBtn>
    ) : (
      <RemoveSpacer />
    )}
  </RowShell>
);

const CastCrewWrapper = styled.div`
  width: 100%;
  flex-direction: column;
`;

const CastCrewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.6rem;
  width: 100%;
  align-items: stretch;

  @media (max-width: 64em) {
    grid-template-columns: 1fr;
  }
`;

const ListShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  min-width: 0;
`;

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const RowShell = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.1rem;
  background-color: ${card.innerBg};
  border: 1px solid ${card.border};
  border-radius: 0.9rem;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${colors.primary.hex};
    box-shadow: 0 1px 6px rgba(32, 84, 150, 0.06);
  }

  & .ant-form-item {
    margin-bottom: 0;
  }

  & .ant-form-item-explain {
    font-size: 1.15rem;
    margin-top: 0.3rem;
  }
`;

const Avatar = styled.img`
  flex: 0 0 auto;
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 50%;
  border: 2px solid ${card.innerBg};
  box-shadow: 0 0 0 1px ${card.border};
  object-fit: cover;
`;

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.8rem;

  & .ant-select,
  & .ant-input,
  & .ant-input-affix-wrapper,
  & .ant-select-selector {
    width: 100% !important;
  }
`;

const FieldStack = styled.div`
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem;
  container-type: inline-size;

  @container (max-width: 26rem) {
    grid-template-columns: 1fr;
  }
`;

const RemoveSpacer = styled.div`
  flex: 0 0 auto;
  width: 2.8rem;
  height: 2.8rem;
`;

const RemoveBtn = styled.button`
  flex: 0 0 auto;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: ${colors.text.hex};
  opacity: 0.55;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.8rem;
  transition: background 0.15s, color 0.15s, opacity 0.15s;

  &:hover {
    background: rgba(239, 35, 60, 0.1);
    color: ${colors.error.hex};
    opacity: 1;
  }
`;

const AddButton = styled(Button)`
  height: 4rem;
  border-radius: 0.8rem;
  font-weight: 500;
  border-style: dashed;
  border-color: ${card.border};
  background: transparent;
  color: ${colors.primary.hex};

  &:hover {
    border-color: ${colors.primary.hex} !important;
    color: ${colors.primary.hex} !important;
    background: ${card.accentSoft} !important;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 2.4rem 1.6rem;
  background-color: ${card.innerBg};
  border: 1px dashed ${card.border};
  border-radius: 0.9rem;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 4.4rem;
  height: 4.4rem;
  border-radius: 50%;
  background: ${card.accentSoft};
  color: ${colors.primary.hex};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  margin-bottom: 0.4rem;
`;

const EmptyTitle = styled.div`
  font-size: 1.35rem;
  font-weight: 600;
  color: ${colors.text.hex};
`;

const EmptyHint = styled.div`
  font-size: 1.15rem;
  color: ${colors.text.hex};
  opacity: 0.6;
  line-height: 1.4;
`;

type StepFourProps = {
  visible?: boolean;
  nameOptions: {
    value: string;
    label: string;
    logo?: string;
    id?: string;
    full_name?: string;
    custom_url?: string;
    type?: "professional" | "business";
  }[];
  setNameModalType: SetState<0 | 1 | 2 | 3 | null>;
  professionalProfileSubject$: Subject<string>;
} & StatePair<
  "directors",
  "setDirectors",
  {
    name: string;
    user?: string;
    biography: string;
    id?: string;
    type?: "professional" | "business";
    logo?: string;
  }[]
> &
  StatePair<
    "producers",
    "setProducers",
    {
      name: string;
      user?: string;
      filmography: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  > &
  StatePair<
    "cast",
    "setCast",
    {
      name: string;
      user?: string;
      character: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  > &
  StatePair<
    "crew",
    "setCrew",
    {
      name: string;
      user?: string;
      role: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  >;
